import { FormBuilder, FormGroup, type ValidatorFn, Validators } from '@angular/forms';
import type { AddressRetrieveFormFields } from '../../../core/models/address-retrieve';
import { DEFAULT_PHONE_COUNTRY_CODE } from '../constants/phone-country-codes';
import type {
  AddressFormValue,
  AddressSectionKey,
  ReturnAddressFormValue,
  ShipmentAddressSectionKey,
} from '../models/address-form.model';
import { optionalNoEmojiValidator } from '../../../shared/validators/emoji.validators';
import {
  ADDRESS_LINE_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_OR_FULL_NAME_MAX_LENGTH,
  CONTACT_NAME_MAX_LENGTH,
  SUITE_APT_UNIT_MAX_LENGTH,
  optionalAddressRestrictedCharactersValidator,
  optionalEmailFormatValidator,
  optionalNameRestrictedCharactersValidator,
  requiredEmailValidator,
  requiredTrimmedValidator,
  nameRestrictedCharactersValidator,
  addressRestrictedCharactersValidator,
  EMAIL_MAX_LENGTH,
} from '../validators/address-field.validators';
import {
  canadianPostalCodeValidator,
  POSTAL_CODE_MAX_LENGTH,
} from '../validators/postal-code.validators';
import {
  ENTRY_BUZZER_CODE_MAX_LENGTH,
  optionalEntryBuzzerCodeValidator,
} from '../validators/entry-buzzer-code.validators';
import { civicStreetRequiredWhenSpecialAddressLine1Validator } from '../validators/special-address.validators';
import { containsSpecialAddressKeywordInLine1 } from '../validators/special-address-keywords';
import {
  northAmericanPhoneValidator,
  optionalNorthAmericanPhoneValidator,
} from '../validators/phone.validators';
import {
  optionalPhoneExtensionValidator,
  phoneExtensionRequiresPhoneValidator,
  PHONE_EXTENSION_MAX_LENGTH,
} from '../validators/phone-extension.validators';

/**
 * Returns the default empty value for a full address section.
 */
export function getEmptyAddressFormValue(): AddressFormValue {
  return {
    companyOrFullName: '',
    attentionTo: '',
    streetAddress: '',
    suiteAptUnit: '',
    entryBuzzerCode: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    province: null,
    postalCode: '',
    email: '',
    phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
    phoneNumber: '',
    phoneExtension: '',
    receiveNotifications: false,
    residential: false,
  };
}

/**
 * Returns the default empty value for the return address subsection.
 */
export function getEmptyReturnAddressFormValue(): ReturnAddressFormValue {
  return {
    streetAddress: '',
    suiteAptUnit: '',
    entryBuzzerCode: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    province: null,
    postalCode: '',
    phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
    phoneNumber: '',
    phoneExtension: '',
    residential: false,
  };
}

/**
 * Locks the country code to the default +1 value (disabled, Figma static control).
 */
function lockPhoneCountryCode(addressGroup: FormGroup): void {
  addressGroup.get('phoneCountryCode')?.disable();
}

/**
 * Requires email for Ship From always; for Ship To only when notifications are enabled.
 */
function wireConditionalEmailValidators(addressGroup: FormGroup, section: AddressSectionKey): void {
  const emailControl = addressGroup.get('email');
  const notificationsControl = addressGroup.get('receiveNotifications');

  if (!emailControl || !notificationsControl) {
    return;
  }

  const applyEmailValidators = (): void => {
    const notificationsEnabled = notificationsControl.value === true;
    const requireEmail = section === 'shipFrom' || notificationsEnabled;
    emailControl.setValidators(
      requireEmail
        ? [requiredEmailValidator, Validators.maxLength(EMAIL_MAX_LENGTH)]
        : [optionalEmailFormatValidator, Validators.maxLength(EMAIL_MAX_LENGTH)],
    );
    emailControl.updateValueAndValidity({ emitEvent: false });
  };

  applyEmailValidators();
  notificationsControl.valueChanges.subscribe(() => applyEmailValidators());
}

/**
 * Requires the phone number only when shipment notifications are enabled for the section.
 * When disabled, the phone number is optional but still format-checked if provided.
 */
function wireConditionalPhoneValidators(addressGroup: FormGroup): void {
  const phoneControl = addressGroup.get('phoneNumber');
  const notificationsControl = addressGroup.get('receiveNotifications');

  if (!phoneControl || !notificationsControl) {
    return;
  }

  const applyPhoneValidators = (): void => {
    const notificationsEnabled = notificationsControl.value === true;
    phoneControl.setValidators(
      notificationsEnabled ? [northAmericanPhoneValidator] : [optionalNorthAmericanPhoneValidator],
    );
    phoneControl.updateValueAndValidity({ emitEvent: false });
  };

  applyPhoneValidators();
  notificationsControl.valueChanges.subscribe(() => applyPhoneValidators());
}

/**
 * Validates that a province has been selected.
 */
function requiredProvinceValidator(control: { value: string | null }): { required: true } | null {
  if (control.value === null || control.value === undefined || control.value === '') {
    return { required: true };
  }

  return null;
}

/**
 * Revalidates the civic street field when street address line 1 changes.
 */
function wireSpecialAddressCivicStreetRevalidation(
  group: FormGroup,
  civicFieldName: 'addressLine2' | 'suiteAptUnit',
): void {
  const streetControl = group.get('streetAddress');
  const civicControl = group.get(civicFieldName);
  const suiteControl = civicFieldName === 'addressLine2' ? group.get('suiteAptUnit') : null;

  if (!streetControl || !civicControl) {
    return;
  }

  streetControl.valueChanges.subscribe(() => {
    civicControl.updateValueAndValidity({ emitEvent: false });
  });

  suiteControl?.valueChanges.subscribe(() => {
    civicControl.updateValueAndValidity({ emitEvent: false });
  });
}

/**
 * Revalidates phone extension when the phone number changes (OM-45 extension-without-phone).
 */
function wirePhoneExtensionRequiresPhoneRevalidation(group: FormGroup): void {
  const phoneControl = group.get('phoneNumber');
  const extensionControl = group.get('phoneExtension');

  if (!phoneControl || !extensionControl) {
    return;
  }

  phoneControl.valueChanges.subscribe(() => {
    extensionControl.updateValueAndValidity({ emitEvent: false });
  });
}

/**
 * Expands optional address lines when line 1 requires a civic street in address line 2.
 */
export function expandAddressLinesWhenSpecialStreetRequiresCivic(form: FormGroup): void {
  const sections: ShipmentAddressSectionKey[] = ['shipFrom', 'shipTo'];

  if (form.get('useDifferentReturnAddress')?.value === true) {
    sections.push('returnAddress');
  }

  for (const section of sections) {
    const streetAddress = (form.get(section) as FormGroup | null)?.get('streetAddress')?.value;

    if (typeof streetAddress === 'string' && containsSpecialAddressKeywordInLine1(streetAddress)) {
      expandAddressLines(form, section);
    }
  }
}
const addressLineMaxLengthValidator = Validators.maxLength(ADDRESS_LINE_MAX_LENGTH);
const suiteAptUnitMaxLengthValidator = Validators.maxLength(SUITE_APT_UNIT_MAX_LENGTH);
const cityMaxLengthValidator = Validators.maxLength(CITY_MAX_LENGTH);
const postalCodeMaxLengthValidator = Validators.maxLength(POSTAL_CODE_MAX_LENGTH);
const companyOrFullNameMaxLengthValidator = Validators.maxLength(COMPANY_OR_FULL_NAME_MAX_LENGTH);
const contactNameMaxLengthValidator = Validators.maxLength(CONTACT_NAME_MAX_LENGTH);
const entryBuzzerCodeMaxLengthValidator = Validators.maxLength(ENTRY_BUZZER_CODE_MAX_LENGTH);
const phoneExtensionMaxLengthValidator = Validators.maxLength(PHONE_EXTENSION_MAX_LENGTH);
const emailMaxLengthValidator = Validators.maxLength(EMAIL_MAX_LENGTH);

/**
 * Shared Suite/Apt/Unit, Address Line 2, and Address Line 3 controls for sender, receiver, and return.
 */
function createOptionalAddressLineControls(empty: {
  suiteAptUnit: string;
  addressLine2: string;
  addressLine3: string;
}): {
  suiteAptUnit: [string, ValidatorFn[]];
  addressLine2: [string, ValidatorFn[]];
  addressLine3: [string, ValidatorFn[]];
} {
  return {
    suiteAptUnit: [empty.suiteAptUnit, [suiteAptUnitMaxLengthValidator, optionalNoEmojiValidator]],
    addressLine2: [
      empty.addressLine2,
      [
        addressLineMaxLengthValidator,
        optionalAddressRestrictedCharactersValidator,
        civicStreetRequiredWhenSpecialAddressLine1Validator,
      ],
    ],
    addressLine3: [
      empty.addressLine3,
      [addressLineMaxLengthValidator, optionalAddressRestrictedCharactersValidator],
    ],
  };
}

/**
 * Creates a reactive form group for Ship From or Ship To address fields.
 */
export function createAddressFormGroup(
  formBuilder: FormBuilder,
  section: AddressSectionKey,
): FormGroup {
  const empty = getEmptyAddressFormValue();
  const group = formBuilder.group({
    companyOrFullName: [
      empty.companyOrFullName,
      [
        requiredTrimmedValidator,
        companyOrFullNameMaxLengthValidator,
        nameRestrictedCharactersValidator,
      ],
    ],
    attentionTo: [
      empty.attentionTo,
      [contactNameMaxLengthValidator, optionalNameRestrictedCharactersValidator],
    ],
    streetAddress: [
      empty.streetAddress,
      [
        requiredTrimmedValidator,
        addressLineMaxLengthValidator,
        addressRestrictedCharactersValidator,
      ],
    ],
    ...createOptionalAddressLineControls(empty),
    entryBuzzerCode: [
      empty.entryBuzzerCode,
      [entryBuzzerCodeMaxLengthValidator, optionalEntryBuzzerCodeValidator],
    ],
    city: [
      empty.city,
      [requiredTrimmedValidator, cityMaxLengthValidator, addressRestrictedCharactersValidator],
    ],
    province: formBuilder.control<string | null>(empty.province, requiredProvinceValidator),
    postalCode: [empty.postalCode, [canadianPostalCodeValidator, postalCodeMaxLengthValidator]],
    email: [empty.email, [optionalEmailFormatValidator, emailMaxLengthValidator]],
    phoneCountryCode: empty.phoneCountryCode,
    phoneNumber: [empty.phoneNumber, northAmericanPhoneValidator],
    phoneExtension: [
      empty.phoneExtension,
      [
        phoneExtensionMaxLengthValidator,
        optionalPhoneExtensionValidator,
        phoneExtensionRequiresPhoneValidator,
      ],
    ],
    receiveNotifications: empty.receiveNotifications,
    residential: empty.residential,
  });
  lockPhoneCountryCode(group);
  wireConditionalEmailValidators(group, section);
  wireSpecialAddressCivicStreetRevalidation(group, 'addressLine2');
  wirePhoneExtensionRequiresPhoneRevalidation(group);
  if (section === 'shipTo') {
    wireConditionalPhoneValidators(group);
  }
  return group;
}

/**
 * Creates a reactive form group for the optional return address fields.
 * Phone follows the Ship From input rules but stays optional; rural/PO Box addresses make it
 * required on save via applyRuralRoutePoBoxValidation (OM-693).
 */
export function createReturnAddressFormGroup(formBuilder: FormBuilder): FormGroup {
  const empty = getEmptyReturnAddressFormValue();
  const group = formBuilder.group({
    streetAddress: [
      empty.streetAddress,
      [
        requiredTrimmedValidator,
        addressLineMaxLengthValidator,
        addressRestrictedCharactersValidator,
      ],
    ],
    ...createOptionalAddressLineControls(empty),
    entryBuzzerCode: [
      empty.entryBuzzerCode,
      [entryBuzzerCodeMaxLengthValidator, optionalEntryBuzzerCodeValidator],
    ],
    city: [
      empty.city,
      [requiredTrimmedValidator, cityMaxLengthValidator, addressRestrictedCharactersValidator],
    ],
    province: formBuilder.control<string | null>(empty.province, requiredProvinceValidator),
    postalCode: [empty.postalCode, [canadianPostalCodeValidator, postalCodeMaxLengthValidator]],
    phoneCountryCode: empty.phoneCountryCode,
    phoneNumber: [empty.phoneNumber, optionalNorthAmericanPhoneValidator],
    phoneExtension: [
      empty.phoneExtension,
      [
        phoneExtensionMaxLengthValidator,
        optionalPhoneExtensionValidator,
        phoneExtensionRequiresPhoneValidator,
      ],
    ],
    residential: empty.residential,
  });

  lockPhoneCountryCode(group);
  wireSpecialAddressCivicStreetRevalidation(group, 'addressLine2');
  wirePhoneExtensionRequiresPhoneRevalidation(group);
  return group;
}

/**
 * Creates the root reactive form for the create-shipment address step.
 */
export function createShipmentAddressesForm(formBuilder: FormBuilder): FormGroup {
  return formBuilder.nonNullable.group({
    shipFrom: createAddressFormGroup(formBuilder, 'shipFrom'),
    shipTo: createAddressFormGroup(formBuilder, 'shipTo'),
    returnAddress: createReturnAddressFormGroup(formBuilder),
    useDifferentReturnAddress: false,
    shipFromAddressLinesExpanded: false,
    shipToAddressLinesExpanded: false,
    returnAddressLinesExpanded: false,
  });
}

/** Root form control that tracks the expanded state for each section's optional address lines. */
const ADDRESS_LINES_EXPANDED_CONTROL: Record<ShipmentAddressSectionKey, string> = {
  shipFrom: 'shipFromAddressLinesExpanded',
  shipTo: 'shipToAddressLinesExpanded',
  returnAddress: 'returnAddressLinesExpanded',
};

/**
 * Expands optional address line fields for a section. This action is one-way.
 */
export function expandAddressLines(form: FormGroup, section: ShipmentAddressSectionKey): void {
  form.controls[ADDRESS_LINES_EXPANDED_CONTROL[section]].setValue(true);
}

/**
 * Resets a single address section to its empty defaults without affecting other sections.
 * Replaces the nested form group so kit inputs refresh after clear (OnPush CVA workaround).
 */
export function clearAddressSection(
  form: FormGroup,
  section: ShipmentAddressSectionKey,
  formBuilder: FormBuilder,
): void {
  if (section === 'returnAddress') {
    form.setControl('returnAddress', createReturnAddressFormGroup(formBuilder));
    return;
  }

  form.setControl(section, createAddressFormGroup(formBuilder, section));
}

/**
 * Merges retrieve fields into a section and replaces its form group so OnPush kit inputs refresh.
 */
export function refreshAddressSectionWithRetrieveFields(
  rootForm: FormGroup,
  section: ShipmentAddressSectionKey,
  retrieveFields: AddressRetrieveFormFields,
  formBuilder: FormBuilder,
): void {
  const sectionGroup = rootForm.get(section) as FormGroup;
  const merged = { ...sectionGroup.getRawValue(), ...retrieveFields };
  const refreshed =
    section === 'returnAddress'
      ? createReturnAddressFormGroup(formBuilder)
      : createAddressFormGroup(formBuilder, section);

  refreshed.patchValue(merged);
  rootForm.setControl(section, refreshed);
}
